import { lazy } from 'react';
import { Badge } from '@mui/material';

import { formatDateTime } from '../../../utils/formatting';
import type { ResourceViewDefinition } from '../types';
import { EncounterHeader } from './encounter-header';
import { encounterRecord } from './encounter-record';
import { statusTone } from './status-tone';

/**
 * Encounter view registration (GDN-2723) — replaces the standalone encounter
 * detail page and the bespoke encounters list.
 *
 * Note the list/detail asymmetry this rides on: the canonical `list` returns
 * flat `Encounter` rows (so these columns read fields directly), while `get`
 * returns an `{encounter, appointment}` composite. The `record` normalizer
 * below tells the shell where the record lives inside that payload, so every
 * slot and the Fields tab see the encounter itself.
 */
export const encounterView: ResourceViewDefinition = {
  label: 'Encounter',
  record: encounterRecord,
  header: EncounterHeader,
  preview: lazy(() =>
    import('./encounter-preview').then(m => ({ default: m.EncounterPreview })),
  ),
  sections: lazy(() =>
    import('./encounter-sections').then(m => ({ default: m.EncounterSections })),
  ),
  columns: [
    {
      key: 'status',
      label: 'Status',
      render: value => (
        <Badge color={statusTone(typeof value === 'string' ? value : null)}>
          {typeof value === 'string' ? value : '—'}
        </Badge>
      ),
    },
    { key: 'encounter_type', label: 'Type' },
    { key: 'note_title', label: 'Note title' },
    { key: 'patient_id', label: 'Patient' },
    {
      key: 'start_time',
      label: 'Start',
      render: value => (typeof value === 'string' ? formatDateTime(value) : '—'),
    },
    {
      key: 'is_signed',
      label: 'Signed',
      render: value => (value === true ? 'Yes' : 'No'),
    },
  ],
  filters: [{ key: 'patient_id', label: 'Patient id', type: 'text' }],
};
